import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Command, Search, Keyboard } from 'lucide-react';

interface KeyboardShortcutsProps {
  isOpen: boolean;
  onClose: () => void;
}

interface ShortcutCategory {
  name: string;
  shortcuts: {
    keys: string[];
    description: string;
    context?: string;
  }[];
}

const SHORTCUT_CATEGORIES: ShortcutCategory[] = [
  {
    name: 'General',
    shortcuts: [
      { keys: ['⌘', 'K'], description: 'Open command palette' },
      { keys: ['⌘', 'S'], description: 'Save current document' },
      { keys: ['⌘', '/'], description: 'Toggle AI assistant' },
      { keys: ['⌘', '?'], description: 'Show keyboard shortcuts' },
      { keys: ['Esc'], description: 'Close modal / Cancel action' },
    ],
  },
  {
    name: 'Navigation',
    shortcuts: [
      { keys: ['⌘', '1'], description: 'Go to Dashboard' },
      { keys: ['⌘', '2'], description: 'Go to Templates' },
      { keys: ['⌘', '3'], description: 'Go to Clauses' },
      { keys: ['⌘', '4'], description: 'Go to Clients' },
      { keys: ['⌘', '5'], description: 'Go to Matters' },
      { keys: ['⌘', '6'], description: 'Go to Documents' },
      { keys: ['⌘', '7'], description: 'Go to AI Agents' },
      { keys: ['⌘', '8'], description: 'Go to Rainmaker' },
      { keys: ['⌘', '9'], description: 'Go to Deal Engine' },
      { keys: ['⌘', '0'], description: 'Go to Decisions' },
    ],
  },
  {
    name: 'Template Editor',
    shortcuts: [
      { keys: ['⌘', 'Enter'], description: 'Run preview', context: 'Editor' },
      { keys: ['⌘', 'B'], description: 'Toggle sidebar', context: 'Editor' },
      { keys: ['⌘', 'P'], description: 'Quick preview', context: 'Editor' },
      { keys: ['⌘', 'Shift', 'F'], description: 'Format document', context: 'Editor' },
      { keys: ['⌘', 'D'], description: 'Duplicate line', context: 'Editor' },
      { keys: ['⌘', 'Shift', 'K'], description: 'Delete line', context: 'Editor' },
      { keys: ['Tab'], description: 'Indent selection', context: 'Editor' },
      { keys: ['Shift', 'Tab'], description: 'Outdent selection', context: 'Editor' },
    ],
  },
  {
    name: 'AI Features',
    shortcuts: [
      { keys: ['⌘', 'Shift', 'G'], description: 'Generate template with AI' },
      { keys: ['⌘', 'Shift', 'A'], description: 'Analyze document' },
      { keys: ['⌘', 'Shift', 'F'], description: 'Smart fill variables' },
      { keys: ['⌘', 'Shift', 'C'], description: 'Suggest clauses' },
    ],
  },
  {
    name: 'Document Actions',
    shortcuts: [
      { keys: ['⌘', 'N'], description: 'Create new template' },
      { keys: ['⌘', 'O'], description: 'Open template' },
      { keys: ['⌘', 'Shift', 'S'], description: 'Save as...' },
      { keys: ['⌘', 'E'], description: 'Export document' },
      { keys: ['⌘', 'Shift', 'E'], description: 'Export all selected' },
    ],
  },
  {
    name: 'Selection & Search',
    shortcuts: [
      { keys: ['⌘', 'F'], description: 'Find in document' },
      { keys: ['⌘', 'H'], description: 'Find and replace' },
      { keys: ['⌘', 'G'], description: 'Find next' },
      { keys: ['⌘', 'Shift', 'G'], description: 'Find previous' },
      { keys: ['⌘', 'A'], description: 'Select all' },
      { keys: ['⌘', 'L'], description: 'Select line' },
    ],
  },
];

export function KeyboardShortcuts({ isOpen, onClose }: KeyboardShortcutsProps) {
  const [searchQuery, setSearchQuery] = useState('');
  const [isMac, setIsMac] = useState(true);

  useEffect(() => {
    setIsMac(navigator.platform.toUpperCase().indexOf('MAC') >= 0);
  }, []);

  // Convert Mac symbols to Windows keys if needed
  const convertKey = (key: string): string => {
    if (isMac) return key;
    const conversions: Record<string, string> = {
      '⌘': 'Ctrl',
      '⌥': 'Alt',
      '⇧': 'Shift',
    };
    return conversions[key] || key;
  };

  // Filter shortcuts based on search
  const filteredCategories = searchQuery
    ? SHORTCUT_CATEGORIES.map(cat => ({
        ...cat,
        shortcuts: cat.shortcuts.filter(s =>
          s.description.toLowerCase().includes(searchQuery.toLowerCase()) ||
          s.keys.join(' ').toLowerCase().includes(searchQuery.toLowerCase())
        ),
      })).filter(cat => cat.shortcuts.length > 0)
    : SHORTCUT_CATEGORIES;

  // Register keyboard shortcut to open this modal
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === '?') {
        e.preventDefault();
        // Toggle would be handled by parent
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4"
        onClick={onClose}
      >
        <motion.div
          initial={{ scale: 0.95, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.95, opacity: 0 }}
          className="w-full max-w-3xl max-h-[85vh] bg-primary-900 rounded-xl border border-primary-700 flex flex-col"
          onClick={e => e.stopPropagation()}
        >
          {/* Header */}
          <div className="flex items-center justify-between px-6 py-4 border-b border-primary-700">
            <div className="flex items-center gap-3">
              <Keyboard className="w-6 h-6 text-accent-400" />
              <h2 className="text-xl font-semibold text-white">Keyboard Shortcuts</h2>
            </div>
            <div className="flex items-center gap-4">
              <button
                onClick={() => setIsMac(!isMac)}
                className="text-xs px-2 py-1 bg-primary-700 text-primary-300 rounded hover:bg-primary-600"
              >
                {isMac ? 'Mac' : 'Windows'}
              </button>
              <button onClick={onClose} className="p-2 hover:bg-white/10 rounded-lg">
                <X className="w-5 h-5 text-primary-400" />
              </button>
            </div>
          </div>

          {/* Search */}
          <div className="px-6 py-3 border-b border-primary-700/50">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-primary-400" />
              <input
                type="text"
                placeholder="Search shortcuts..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-9 pr-4 py-2 bg-primary-800/50 border border-primary-700 rounded-lg text-sm text-white placeholder-primary-400 focus:outline-none focus:border-accent-500/50"
              />
            </div>
          </div>

          {/* Shortcuts Grid */}
          <div className="flex-1 overflow-auto p-6">
            <div className="grid grid-cols-2 gap-6">
              {filteredCategories.map(category => (
                <div key={category.name}>
                  <h3 className="text-sm font-semibold text-accent-400 uppercase tracking-wider mb-3">
                    {category.name}
                  </h3>
                  <div className="space-y-2">
                    {category.shortcuts.map((shortcut, idx) => (
                      <div
                        key={idx}
                        className="flex items-center justify-between py-2 px-3 bg-primary-800/30 rounded-lg hover:bg-primary-800/50 transition-colors"
                      >
                        <div className="flex items-center gap-2">
                          <span className="text-sm text-primary-200">{shortcut.description}</span>
                          {shortcut.context && (
                            <span className="text-xs px-1.5 py-0.5 bg-primary-700 text-primary-400 rounded">
                              {shortcut.context}
                            </span>
                          )}
                        </div>
                        <div className="flex items-center gap-1">
                          {shortcut.keys.map((key, keyIdx) => (
                            <span key={keyIdx}>
                              <kbd className="px-2 py-1 bg-primary-700 border border-primary-600 rounded text-xs font-mono text-primary-200 shadow-sm">
                                {convertKey(key)}
                              </kbd>
                              {keyIdx < shortcut.keys.length - 1 && (
                                <span className="text-primary-500 mx-0.5">+</span>
                              )}
                            </span>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>

            {filteredCategories.length === 0 && (
              <div className="text-center py-12 text-primary-400">
                <Keyboard className="w-12 h-12 mx-auto mb-3 opacity-50" />
                <p>No shortcuts found for "{searchQuery}"</p>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="px-6 py-3 border-t border-primary-700 bg-primary-900/50">
            <div className="flex items-center justify-between text-xs text-primary-400">
              <span className="flex items-center gap-1">
                Press <kbd className="px-1.5 py-0.5 bg-primary-700 rounded font-mono">{isMac ? '⌘' : 'Ctrl'}</kbd>
                <span>+</span>
                <kbd className="px-1.5 py-0.5 bg-primary-700 rounded font-mono">?</kbd> to toggle this panel
              </span>
              <span>
                {SHORTCUT_CATEGORIES.reduce((acc, cat) => acc + cat.shortcuts.length, 0)} shortcuts available
              </span>
            </div>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}

// Hook to register global keyboard shortcuts
export function useKeyboardShortcuts(shortcuts: Record<string, () => void>) {
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      const key = [
        e.metaKey || e.ctrlKey ? 'mod' : '',
        e.shiftKey ? 'shift' : '',
        e.altKey ? 'alt' : '',
        e.key.toLowerCase(),
      ].filter(Boolean).join('+');

      if (shortcuts[key]) {
        e.preventDefault();
        shortcuts[key]();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [shortcuts]);
}
